import { isEmpty } from 'lodash'
import { SET_IS_LOADING_CONTRACT_DATA, SET_CONTRACT_DATA } from './constants.js'
import { db, getStoredContract, storeContract } from '../db'
import { EventBus, Events } from '../../eventBus'
import { findContractConflicts } from './utils'

/**
 * Custom error class for contract-related errors
 */
class ContractError extends Error {
	constructor(message, type, data = {}) {
		super(message)
		this.name = 'ContractError'
		this.type = type
		Object.assign(this, data)
	}
}

/**
 * Get a contract by token
 */
export const getContract = function ({ dispatch, commit, state }, payload = {}) {
	commit(SET_IS_LOADING_CONTRACT_DATA, true)

	return new Promise(async (resolve, reject) => {
		try {
			// Check for stored contract first to see if we have local changes
			const storedContract = await getStoredContract(payload.token)
			const hasLocalChanges = storedContract?.data?.hasOfflineChanges

			// On initial load or when online, try to fetch from server first
			if ((payload.isInitialLoad || navigator.onLine) && !payload.forceOffline) {
				try {
					const { contract: contractState } = state
					const apiPayload = {
						apiEndpoint: 'contract',
						postData: {
							action: 'get_contract',
							token: payload.token,
							version: contractState?.data?.version || null
						}
					}

					const response = await dispatch('apiCall', apiPayload)

					if (response.data?.payload) {
						const contractData = response.data.payload

						// If we have local changes, don't overwrite them with server data
						if (hasLocalChanges) {
							commit(SET_IS_LOADING_CONTRACT_DATA, false)
							commit(SET_CONTRACT_DATA, storedContract.data)
							return resolve(response)
						}

						// Store in IndexedDB for offline access
						await storeContract(payload.token, {
							...contractData,
							lastFetched: Date.now()
						})

						commit(SET_CONTRACT_DATA, contractData)
						commit(SET_IS_LOADING_CONTRACT_DATA, false)
						return resolve(response)
					}

					throw new Error('Invalid contract data received')
				} catch (apiError) {
					// If we're offline or API call fails, fall back to stored data
					if (!navigator.onLine || apiError.message.includes('Failed to fetch')) {
						if (storedContract) {
							commit(SET_CONTRACT_DATA, storedContract.data)
							commit(SET_IS_LOADING_CONTRACT_DATA, false)
							return resolve()
						}

						throw new Error('No stored contract found and device is offline')
					}

					// If it's a different error, propagate it
					throw apiError
				}
			} else {
				// Offline mode - get from IndexedDB
				if (storedContract) {
					commit(SET_CONTRACT_DATA, storedContract.data)
					commit(SET_IS_LOADING_CONTRACT_DATA, false)
					resolve()
				} else {
					throw new Error('No stored contract found and device is offline')
				}
			}
		} catch (error) {
			console.error('Error fetching contract:', error)
			commit(SET_CONTRACT_DATA, null)
			commit(SET_IS_LOADING_CONTRACT_DATA, false)
			reject(error)
		}
	})
}

/**
 * Clear stored contract data
 */
export const clearStoredContract = async function ({ commit }, token) {
	await db.contracts.delete(token)
	commit(SET_CONTRACT_DATA, null)
}

/**
 * Update a contract
 */
export const updateContract = function ({ dispatch, commit }, payload = {}) {
	commit(SET_IS_LOADING_CONTRACT_DATA, true)

	return new Promise(async (resolve, reject) => {
		try {
			const storedContract = await getStoredContract(payload.token)

			// If force update is specified, use server version
			const baseVersion = payload.forceUpdate
				? payload.serverContract?.version
				: storedContract?.data?.version || 0

			// Create merged contract data with ONLY the changed fields
			const baseContract = payload.forceUpdate ? payload.serverContract : storedContract?.data || {}

			const updatedContractData = {
				...baseContract,
				version: baseVersion
			}

			// Only merge specific changed fields
			Object.keys(payload.contractData).forEach((field) => {
				if (field === 'inventory_list' && payload.contractData[field]?.items) {
					updatedContractData.inventory_list = {
						...payload.contractData.inventory_list,
						items: payload.contractData.inventory_list.items.filter(
							(item) => item.description?.trim() !== ''
						)
					}
				} else {
					updatedContractData[field] = payload.contractData[field]
				}
			})

			// If offline, store locally and mark as having unsaved changes
			if (!navigator.onLine) {
				const updatedData = {
					...updatedContractData,
					hasOfflineChanges: true,
					lastModified: Date.now()
				}
				await storeContract(payload.token, updatedData)
				commit(SET_CONTRACT_DATA, updatedData)
				commit(SET_IS_LOADING_CONTRACT_DATA, false)
				EventBus.$emit(Events.CONTRACT_UNSAVED_CHANGES, true)
				return resolve({ data: { payload: updatedData } })
			}

			updatedContractData.version = baseVersion

			// Prepare API payload
			const apiPayload = {
				apiEndpoint: 'contract',
				postData: {
					action: 'update_contract',
					token: payload.token,
					contract: updatedContractData,
					base_version: baseVersion
				}
			}

			try {
				const response = await dispatch('apiCall', apiPayload)

				if (response.data.result === 'success') {
					const updatedData = {
						...response.data.payload?.contract,
						hasOfflineChanges: false,
						lastModified: Date.now()
					}
					await storeContract(payload.token, updatedData)
					commit(SET_CONTRACT_DATA, updatedData)
					commit(SET_IS_LOADING_CONTRACT_DATA, false)
					EventBus.$emit(Events.CONTRACT_UNSAVED_CHANGES, false)
					resolve(response)
				} else if (response.data.result === 'failure') {
					const serverContract = response.data.payload?.contract
					const conflicts = findContractConflicts(
						storedContract?.data,
						serverContract,
						updatedContractData
					)
					// Handle version conflict
					throw new ContractError(
						'Contract has been modified since your last view. Please review changes before updating.',
						'MERGE_REQUIRED',
						{
							conflicts: conflicts,
							serverContract: serverContract,
							pendingChanges: updatedContractData
						}
					)
				} else {
					throw new Error(response.data.message || 'Failed to update contract')
				}
			} catch (error) {
				if (error.type === 'MERGE_REQUIRED') {
					// Pass through merge conflicts
					throw error
				}

				// For other errors, if we have offline changes or are offline, keep the changes
				if (storedContract?.data?.hasOfflineChanges || !navigator.onLine) {
					const updatedData = {
						...updatedContractData,
						hasOfflineChanges: true,
						lastModified: Date.now()
					}
					await storeContract(payload.token, updatedData)
					commit(SET_CONTRACT_DATA, updatedData)
					EventBus.$emit(Events.CONTRACT_UNSAVED_CHANGES, true)
				}
				throw error
			}
		} catch (error) {
			commit(SET_IS_LOADING_CONTRACT_DATA, false)
			reject(error)
		}
	})
}

// Add a new action to sync offline changes
export const syncOfflineChanges = function ({ dispatch }, payload = {}) {
	return new Promise(async (resolve, reject) => {
		try {
			const storedContract = await getStoredContract(payload.token)

			if (storedContract?.data?.hasOfflineChanges) {
				// Try to sync the changes
				await dispatch('updateContract', {
					token: payload.token,
					contractData: storedContract.data
				})
			}
			resolve()
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * Send contract email
 */
export const sendEmail = function ({ dispatch, commit }, options = {}) {
	commit(SET_IS_LOADING_CONTRACT_DATA, true)
	const { token, type } = options
	return new Promise(async (resolve) => {
		try {
			let apiPayload = {
				apiEndpoint: 'contract',
				postData: {
					action: 'send_contract',
					token: token,
					type: type
				}
			}

			dispatch('apiCall', apiPayload).then(async (response) => {
				commit(SET_IS_LOADING_CONTRACT_DATA, false)
				resolve(response)
			})
		} catch (error) {
			console.error('Error sending contract email:', error)
			commit(SET_IS_LOADING_CONTRACT_DATA, false)
			resolve({ error })
		}
	})
}

/**
 * Sign contract as customer
 */
export const signContract = function ({ dispatch, commit }, payload) {
	commit(SET_IS_LOADING_CONTRACT_DATA, true)

	return new Promise(async (resolve) => {
		try {
			let apiPayload = {
				apiEndpoint: 'contract',
				postData: {
					action: 'sign_contract',
					context: payload.context,
					token: payload.token,
					customer_signature: payload.customerSignature
				}
			}

			dispatch('apiCall', apiPayload).then(async (response) => {
				if (!isEmpty(response.data.payload)) {
					const contractData = response.data.payload.contract
					// Store in IndexedDB
					await storeContract(payload.token, contractData)
					// Update Vuex state
					commit(SET_CONTRACT_DATA, contractData)
				}
				commit(SET_IS_LOADING_CONTRACT_DATA, false)
				resolve(response)
			})
		} catch (error) {
			console.error('Error signing contract:', error)
			commit(SET_IS_LOADING_CONTRACT_DATA, false)
			resolve({ error })
		}
	})
}
